/**
 * Dark Mode Color Palette
 * Optimized colors for dark theme
 */

export const darkColors = {
    palette: {
        mode: 'dark',

        primary: {
            main: "#80cbc4",       // Teal 200 - Very bright for dark backgrounds
            dark: "#4db6ac",       // Teal 300
            light: "#B2DFDB",      // Teal 100
            contrastText: "#FFFFFF",
        },

        secondary: {
            main: "#FF7043",      // Warm accent color
            dark: "#F4511E",
            light: "#FF8A65",
            contrastText: "#000000",
        },

        background: {
            default: "#121212",   // True dark background
            paper: "#1E1E1E",     // Elevated surface
        },

        text: {
            primary: "#FFFFFF",       // White text
            secondary: "#B0B0B0",     // Muted text
            disabled: "#6E6E6E",      // Disabled text
        },

        status: {
            success: "#66BB6A",   // Green (adjusted for dark mode)
            warning: "#FFA726",   // Orange
            error: "#EF5350",     // Red (adjusted for dark mode)
            errorDark: "#C62828",
        },

        grey: {
            background: "#2C2C2C",    // Light grey background
            border: "#3E3E3E",        // Border color
            borderDark: "#2A2A2A",    // Darker border
            dark: "#757575",
        },

        common: {
            black: "#000000",
            white: "#FFFFFF",
        },

        custom: {
            sidebarBg: "#1A1A1A",
            fulfilled: "#66BB6A",
            unfulfilled: "#EF5350",
            divider: "rgba(255, 255, 255, 0.12)",
        },
    },
};

/**
 * Light Mode Color Palette (existing)
 * This is your current theme
 */
export const lightColors = {
    palette: {
        mode: 'light',

        primary: {
            main: "#00796B",
            dark: "#004D40",
            light: "#4DB6AC",
            contrastText: "#FFFFFF",
        },

        secondary: {
            main: "#FF6F00",
            dark: "#E65100",
            light: "#FF8F00",
            contrastText: "#FFFFFF",
        },

        background: {
            default: "#FFFFFF",
            paper: "#F8F9FA",
        },

        text: {
            primary: "#343a40",
            secondary: "#4f4f4f",
            disabled: "#9E9E9E",
        },

        status: {
            success: "#00796B",
            warning: "#FF6F00",
            error: "#D32F2F",
            errorDark: "#C62828",
        },

        grey: {
            background: "#F5F5F5",
            border: "#E0E0E0",
            borderDark: "#BDBDBD",
            dark: "#757575",
        },

        common: {
            black: "#000000",
            white: "#FFFFFF",
        },

        custom: {
            sidebarBg: "#34495E",
            fulfilled: "#4CAF50",
            unfulfilled: "#F44336",
        },
    },
};

// Export default as light theme (current behavior)
// export const colors = lightColors; // REPLACED by flatColors below for legacy compatibility

/**
 * Legacy Color Constants
 * Kept for backward compatibility
 */
export const WHITE = "#ffffff";
export const BLACK = "#0c0a0a";
export const LIGHT_GREY = "#8d8d95";
export const MARS = "#134b50";
export const SKY_BLUE = lightColors.palette.primary.main;

/**
 * Flat Color Object
 * Replicates the legacy GenericThemeColors structure.
 * @deprecated Use theme.palette for true Dark Mode support.
 */
export const flatColors = {
    // Primary colors
    primary: lightColors.palette.primary.main,
    primaryDark: lightColors.palette.primary.dark,
    primaryLight: lightColors.palette.primary.light,

    // Status colors
    success: lightColors.palette.status.success,
    warning: lightColors.palette.status.warning,
    error: lightColors.palette.status.error,
    errorDark: lightColors.palette.status.errorDark,

    // Background colors
    surface: lightColors.palette.background.paper,
    background: lightColors.palette.background.default,
    backgroundLight: lightColors.palette.grey.background,

    // Border colors
    border: lightColors.palette.grey.border,
    borderDark: lightColors.palette.grey.borderDark,

    // Text colors
    text: {
        primary: lightColors.palette.text.primary,
        secondary: lightColors.palette.text.secondary,
        muted: lightColors.palette.text.disabled,
        white: '#ffffff',
    },

    // Custom colors
    custom: lightColors.palette.custom,

    // Aliases
    headerBg: lightColors.palette.primary.main,
    topBar: lightColors.palette.primary.main,

    // Legacy Aliases
    WHITE: WHITE,
    BLACK: BLACK,
    LIGHT_GREY: LIGHT_GREY,
    SKY_BLUE: SKY_BLUE,
    MARS: MARS,
};

/**
 * Compact Colors
 * Legacy alias used in CreateOrders
 */
export const compactColors = {
    primary: flatColors.primary,
    primaryDark: flatColors.primaryDark,
    primaryLight: flatColors.primaryLight,
    secondary: lightColors.palette.grey.dark,

    success: flatColors.success,
    warning: flatColors.warning,
    error: flatColors.error,
    info: '#17a2b8',
    mars: flatColors.MARS,

    background: flatColors.background,
    backgroundLight: flatColors.backgroundLight,
    surface: flatColors.surface,

    border: flatColors.border,
    hover: 'rgba(0, 0, 0, 0.04)',
    active: 'rgba(0, 0, 0, 0.08)',
    shadow: 'rgba(0, 0, 0, 0.1)',

    text: {
        primary: flatColors.text.primary,
        secondary: flatColors.text.secondary,
        muted: flatColors.text.muted,
        white: flatColors.text.white,
    },

    headerBg: flatColors.headerBg,
    headerText: '#FFFFFF',
    breadcrumbText: '#BDC3C7',
};

// Export 'colors' as the flat object to match legacy imports
export const colors = flatColors;

export { lightColors as default };
